import { DOCUMENT } from '@angular/common';
import { HttpStatusCode } from '@angular/common/http';
import { Injectable, Signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params, Router, UrlTree } from '@angular/router';
import { App } from '@df/core/app';
import { Search } from '@df/search/search.interfaces';
import { SEARCH_PATH } from '@df/search/search.resolver';
import compact from 'lodash-es/compact';
import flatten from 'lodash-es/flatten';
import head from 'lodash-es/head';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import pick from 'lodash-es/pick';
import uniq from 'lodash-es/uniq';
import { AdvancedCommerce } from '../advanced-commerce.namespace';
import { AdvancedCommerceService, joinWithSlash } from '../advanced-commerce.service';
import { AdvancedCommerceStore } from './advanced-commerce-store.namespace';

@Injectable({
    providedIn: 'root'
})
export class AdvancedCommerceStoreService {
    protected app = inject(App);
    protected advancedCommerceService = inject(AdvancedCommerceService);
    protected activatedRoute = inject(ActivatedRoute);
    protected router = inject(Router);
    protected document = inject(DOCUMENT);

    /**
     * Get items from response.
     *
     * @param type - Type of items
     * @param response {AdvancedCommerceStore.Response.Response} - Response
     * @param element {AdvancedCommerceStore.Element} - Element
     * @returns {AdvancedCommerceStore.Response.Item[]}
     */
    getItems(
        type: AdvancedCommerce.Query.ResponseElementType,
        response: AdvancedCommerceStore.Response.Response,
        element: AdvancedCommerceStore.Element
    ): AdvancedCommerceStore.Items.Item[] {
        if (response?.elements?.[element]?.[type]?.results) {
            return (
                response.elements[element][type].results.map(result => {
                    return {
                        id: result.item.id,
                        name:
                            result.item.type === 'advert' && result.item.data
                                ? result.item.data['title']
                                : result.item.titles?.default || 'No title',
                        subtitle: result.item.type === 'advert' && result.item?.data?.['subtitle'],
                        url: result.item.type === 'advert' && result.item.data ? result.item.data['click_url'] : result.item.url,
                        image:
                            result.item.type === 'advert' && result.item.data
                                ? result.item.data['image_url']
                                : result.item.media?.image.src || null,
                        advert_type: result.item.advert_type,
                        type: result.item.type,
                        size: result.item.size || 1,
                        term: result.item.search_term
                    };
                }) || []
            );
        }

        return [];
    }

    /**
     * Get meta from response.
     *
     * @param type {AdvancedCommerce.Query.ResponseElementType} - Type of meta
     * @param response {AdvancedCommerceStore.Response.Response} - Response
     * @param element {AdvancedCommerceStore.Element} - Element
     * @returns {AdvancedCommerceStore.Meta.State}
     */
    getMeta(
        type: AdvancedCommerce.Query.ResponseElementType,
        response: AdvancedCommerceStore.Response.Response,
        element: AdvancedCommerceStore.Element
    ): AdvancedCommerceStore.Meta.State {
        const result = response?.elements?.[element]?.[type];

        return {
            metaAdvertCount: result?.meta?.advert_count || 0,
            metaClickCode: result?.meta?.click_code || '',
            metaCount: result?.meta?.count || 0,
            metaCurrentPage: result?.meta?.current_page || 1,
            metaIsPagingRequest: false,
            metaFrom: result?.meta?.from || 1,
            metaPageCount: result?.meta?.page_count || 1,
            metaPageSize: result?.meta?.page_size || 24,
            metaProductCount: result?.meta?.product_count || 0,
            metaTo: result?.meta?.to || 24
        };
    }

    /**
     * Get filters from response.
     *
     * @param type {AdvancedCommerce.Query.ResponseElementType} - Type of meta
     * @param response {AdvancedCommerce.Query.Response} - Response
     * @param element {AdvancedCommerceStore.Element} - Element
     * @returns {AdvancedCommerceStore.Filters.Filter[]}
     */
    getFilters(
        type: AdvancedCommerce.Query.ResponseElementType,
        response: AdvancedCommerceStore.Response.Response,
        element: AdvancedCommerceStore.Element
    ): AdvancedCommerceStore.Filters.Filter[] | undefined {
        if (response?.elements?.[element]?.[type]?.facets) {
            return (
                response.elements[element][type].facets.map(facet => {
                    const code = facet.properties?.magento_attribute_code;
                    const attribute = code ? this.app.resources.attribute.get(code) : undefined;

                    const filter: AdvancedCommerceStore.Filters.Filter = {
                        id: facet.id,
                        label: facet.titles?.default || 'No label',
                        type: facet.field_type,
                        code,
                        selected: !!facet.selected,
                        selectedCount: facet.selected_count || 0,
                        isSwatch: !!attribute?.data?.isSwatch
                    };

                    if (facet.field_type === 'price') {
                        filter.type = 'range';
                        filter.meta = pick(facet?.meta, 'from', 'to', 'min', 'max');
                    } else {
                        filter.options =
                            facet.items?.map(item => {
                                const option: AdvancedCommerceStore.Filters.Option = {
                                    id: item.id,
                                    value: Number(item.id),
                                    label: item.titles?.default || 'No label',
                                    selected: !!item.selected,
                                    count: item.count || 0,
                                    hasColor: false,
                                    hasImage: false
                                };

                                const image = item.media?.default?.src;
                                const hex = item.properties?.hex;

                                if (image) {
                                    option.swatch = image;
                                    option.hasImage = true;
                                } else if (hex) {
                                    option.swatch = hex;
                                    option.hasColor = true;
                                }

                                return option;
                            }) || [];
                    }

                    return filter;
                }) || []
            );
        }

        return undefined;
    }

    /**
     * Get filters selection from response.
     *
     * @param type {AdvancedCommerce.Query.ResponseElementType} - Type of meta
     * @param response {AdvancedCommerceStore.Response.Response} - Response
     * @param element {AdvancedCommerceStore.Element} - Element
     * @returns {AdvancedCommerceStore.Filters.Selection[]}
     */
    getFiltersSelection(
        type: AdvancedCommerce.Query.ResponseElementType,
        response: AdvancedCommerceStore.Response.Response,
        element: AdvancedCommerceStore.Element
    ): AdvancedCommerceStore.Filters.Selection[] | undefined {
        if (response?.elements?.[element]?.[type]?.filters?.all) {
            const selection: AdvancedCommerceStore.Filters.Selection[] = [];

            response.elements[element][type].filters.all.forEach(filter => {
                // if filter not exists in `selection` then add
                if (!selection.some(s => s.id === filter.id)) {
                    if (filter.type === 'range') {
                        const from = filter.items?.[0]?.from;
                        const to = filter.items?.[0]?.to;

                        if (from || to) {
                            selection.push({
                                id: filter.id,
                                type: filter.type,
                                items: [
                                    {
                                        id: filter.id,
                                        from,
                                        to
                                    }
                                ]
                            });
                        }
                    } else {
                        selection.push({
                            id: filter.id,
                            type: filter.type,
                            items: []
                        });

                        // add only unique items
                        filter.items?.forEach(item => {
                            if (!selection.find(s => s.id === filter.id)?.items.some(i => i.id === item.id)) {
                                selection
                                    .find(s => s.id === filter.id)
                                    ?.items.push({
                                        id: item.id,
                                        from: item.from,
                                        to: item.to
                                    });
                            }
                        });
                    }
                } else {
                    if (filter.type === 'range') {
                        const from = filter.items?.[0]?.from;
                        const to = filter.items?.[0]?.to;

                        if (from || to) {
                            selection
                                .find(s => s.id === filter.id)
                                ?.items.push({
                                    id: filter.id,
                                    from,
                                    to
                                });
                        } else {
                            const _selection = selection.find(s => s.id === filter.id);

                            if (_selection) {
                                _selection.items = [];
                            }
                        }
                    } else {
                        // if filter exists in `selection` then update only unique items
                        filter.items?.forEach(item => {
                            if (
                                !selection.find(s => s.id === filter.id)?.items.some(i => i.id === item.id) &&
                                !selection.find(s => s.id === filter.id)?.items.some(i => i.from === item.from && i.to === item.to)
                            ) {
                                selection
                                    .find(s => s.id === filter.id)
                                    ?.items.push({
                                        id: item.id,
                                        from: item.from,
                                        to: item.to
                                    });
                            }
                        });
                    }
                }
            });

            return selection.filter(s => s.items?.length > 0);
        }

        return undefined;
    }

    /**
     * Get sorting from response.
     *
     * @param response {AdvancedCommerceStore.Response.Response} - Response
     * @returns {AdvancedCommerceStore.Sorting.Item[]}
     */
    getSorting(response: AdvancedCommerceStore.Response.Response): AdvancedCommerceStore.Sorting.Item[] {
        return (
            Object.values(response?.settings?.sorting?.sort || {})
                ?.map(value => {
                    const id = value?.replace('~', '_');

                    return {
                        id,
                        code: value,
                        label: response?.labels?.plp?.sorting[id] || '',
                        active: id === 'default'
                    };
                })
                .filter(o => o.code.length && o.label.length) || []
        );
    }

    /**
     * Get no results from response.
     *
     * @param response {AdvancedCommerceStore.Response.Response} - Response
     * @returns {AdvancedCommerceStore.Search.NoResult}
     */
    getNoResults(response: AdvancedCommerceStore.Response.Response): AdvancedCommerceStore.Search.NoResults {
        const results: AdvancedCommerceStore.Search.NoResults = {
            label: response?.labels?.autocomplete?.no_results?.title || 'No results found. Try another search term.',
            items: []
        };

        Object.keys(response?.labels?.autocomplete?.no_results || {}).forEach(key => {
            if (key.match('^search_tip') && response?.labels?.autocomplete?.no_results[key]) {
                results.items.push(response?.labels?.autocomplete?.no_results[key]);
            }
        });

        return results;
    }

    /**
     * Get popular searches from response.
     *
     * @param response {AdvancedCommerceStore.Response.Response} - Response
     * @param element {AdvancedCommerceStore.Element} - Element
     * @returns {AdvancedCommerceStore.Search.PopularSearch}
     */
    getPopularSearches(
        response: AdvancedCommerceStore.Response.Response,
        element: AdvancedCommerceStore.Element
    ): AdvancedCommerceStore.Search.PopularSearches {
        let items: string[] = compact(this.getItems('popular_searches', response, element)?.map(i => i.term));
        let label: string = response?.labels?.autocomplete.headings['suggested_searches'] || 'Suggested searches';

        if (isEmpty(items) || items?.length === 0) {
            const manualSearches =
                response?.settings?.autocomplete?.['manual_searches_' + this.lang] ||
                response?.settings?.autocomplete?.['manual_searches'] ||
                {};

            items = Object.values(manualSearches) || [];
            label = response?.labels?.autocomplete?.headings['popular_searches'] || 'Popular searches';
        }

        return {
            label,
            items
        };
    }

    /**
     * Get categories searches from response.
     *
     * @param response {AdvancedCommerceStore.Response.Response} - Response
     * @param element {AdvancedCommerceStore.Element} - Element
     * @returns {AdvancedCommerceStore.Search.Categories}
     */
    getSearchesCategories(
        response: AdvancedCommerceStore.Response.Response,
        element: AdvancedCommerceStore.Element
    ): AdvancedCommerceStore.Search.Categories {
        let items: {
            text: string;
            link: string;
        }[] =
            response?.elements?.['autocomplete']?.categories?.results?.map(i => ({
                text: i.item?.titles?.default || '',
                link: i.item.url || ''
            })) || [];
        let label: string = response?.labels?.autocomplete.headings['category_searches'] || 'Suggested categories';

        return {
            label,
            items
        };
    }

    /**
     * Get help from response.
     *
     * @param response {AdvancedCommerceStore.Response.Response} - Response
     * @returns {AdvancedCommerceStore.Search.Help}
     */
    getHelp(response: AdvancedCommerceStore.Response.Response): AdvancedCommerceStore.Search.Help {
        const help = response?.settings?.autocomplete?.['help_' + this.lang] || response?.settings?.autocomplete?.['help'] || {};
        let label: string =
            response?.labels?.autocomplete.headings['help_' + this.lang] ||
            response?.labels?.autocomplete.headings['help'] ||
            'Can we help?';
        const items: { text: string; link: string }[] = [];

        for (let i = 1; i <= 10; i++) {
            if (!isEmpty(help[`slot${i}_text`])) {
                items.push({
                    text: help[`slot${i}_text`] as string,
                    link: help[`slot${i}_link`] || ''
                });
            }
        }

        return {
            label,
            items
        };
    }

    /**
     * Get autocomplete headings from response.
     *
     * @param response {AdvancedCommerceStore.Response.Response} - Response
     * @returns {AdvancedCommerceStore.Search.AutocompleteHeadings}
     */
    getAutocompleteHeadings(response: AdvancedCommerceStore.Response.Response): AdvancedCommerceStore.Search.AutocompleteHeadings {
        return response?.labels?.autocomplete.headings || {};
    }

    /**
     * Get redirect from response.
     *
     * @param type {AdvancedCommerce.Query.ResponseElementType} - Type of meta
     * @param response {AdvancedCommerceStore.Response.Response} - Response
     * @param element {AdvancedCommerceStore.Element} - Element
     * @returns {string | null}
     */
    getRedirect(
        type: AdvancedCommerce.Query.ResponseElementType,
        response: AdvancedCommerceStore.Response.Response,
        element: AdvancedCommerceStore.Element,
        currentRedirect?: string
    ): AdvancedCommerceStore.Response.Redirect | undefined {
        if (response?.header?.status === HttpStatusCode.Found && response?.header?.redirect_url) {
            return {
                url: response?.header?.redirect_url
            };
        }

        if (response?.elements?.[element]?.[type]?.filters?.clear_all_url) {
            const [_tmp, params] = response.elements[element][type].filters.clear_all_url.split('?');
            const _params = {};

            params?.split('&').forEach(param => {
                const [key, value] = param.split('=');

                _params[key] = value;
            });

            const redirect = _params['q'];

            const filtersSelection = this.getFiltersSelection(type, response, element);

            let queryParams: Params | undefined;

            if (filtersSelection !== undefined) {
                queryParams = this.convertSelectionToQueryParams(filtersSelection);
            }

            if (!isEmpty(redirect) && redirect !== currentRedirect) {
                return {
                    url: `${SEARCH_PATH}/${redirect}`,
                    queryParams
                };
            }
        }

        return undefined;
    }

    getCurrentPage(
        type: AdvancedCommerce.Query.ResponseElementType,
        response: AdvancedCommerceStore.Response.Response,
        element: AdvancedCommerceStore.Element,
        currentPage?: string
    ): string | undefined {
        if (response?.elements?.[element]?.[type]?.filters?.clear_all_url) {
            if (!currentPage) {
                let [_currentHref, currentParams] = this.advancedCommerceService.pageUrl.split('?');

                const _params = {};

                currentParams?.split('&').forEach(param => {
                    const [key, value] = param.split('=');

                    _params[key] = value;
                });

                if (_params['pg']) {
                    currentPage = _params['pg'];
                }
            }

            const [_tmp, params] = response.elements[element][type].filters.clear_all_url.split('?');
            const queryParams = {};

            params?.split('&').forEach(param => {
                const [key, value] = param.split('=');

                queryParams[key] = value;
            });

            let pg = queryParams['pg'];

            if (!pg) {
                pg = response.elements[element][type]?.meta?.current_page;
            }

            if (pg) {
                return pg.toString();
            } else if (currentPage) {
                return currentPage;
            }
        }

        return undefined;
    }

    /**
     * Return search `page_url` in format `/search/term`
     */
    getSearchPageUrl(request: Search.IRequest) {
        const queryParams = { ...request.queryParams }; // params from resolver
        let [currentHref, currentParams] = this.advancedCommerceService.pageUrl.split('?');
        const currentTerm = currentHref.split(`${SEARCH_PATH}/`)[1];

        // reset all query params if term change
        if (currentTerm && request?.q !== currentTerm) {
            currentParams = '';
        }

        const params: string[] = flatten(
            Object.keys(queryParams).map(key => {
                if (Array.isArray(queryParams[key])) {
                    return queryParams[key].map(value => `${key}=${value}`);
                }

                return `${key}=${queryParams[key]}`;
            })
        );

        return joinWithSlash(
            this.document.location.origin,
            `${SEARCH_PATH}/${request?.q || currentTerm}${params.length && request.resolver ? `?${params.join('&')}` : currentParams?.length && !request.resolver ? `?${currentParams}` : ''}`
        );
    }

    setFiltersSelection(filtersSelection?: AdvancedCommerceStore.Filters.Selection[]) {
        const urlTree = this.getUrlTree();

        const preventQueryParams = ['sort', 'pg', 'q'];

        if (filtersSelection) {
            if (filtersSelection.length === 0) {
                // remove all filters
                Object.keys(urlTree.queryParams).forEach(key => {
                    if (!preventQueryParams.includes(key)) {
                        delete urlTree.queryParams[key];
                    }
                });
            } else {
                // delete all query params except prevented query params and selected filters
                for (const key of Object.keys(urlTree.queryParams)) {
                    if (!preventQueryParams.includes(key) && !filtersSelection.some(filter => filter.id === key)) {
                        delete urlTree.queryParams[key];
                    }
                }

                filtersSelection.forEach(filter => {
                    let values: string | string[];

                    if (filter.type === 'range') {
                        const item = head(filter.items);

                        values = `${item?.from || ''}-${item?.to || ''}`;
                    } else {
                        values = uniq(filter.items.map(item => item.id));
                    }

                    if (values?.length) {
                        urlTree.queryParams[filter.id] = values;
                    } else {
                        delete urlTree.queryParams[filter.id];
                    }
                });
            }
        }

        this.updateQueryParams(urlTree.queryParams);
    }

    /**
     * Set current page query param.
     *
     * @param currentPage {number} - Current page
     * @returns void
     */
    setCurrentPage(currentPage?: number): void {
        const urlTree = this.getUrlTree();

        if (currentPage === undefined || currentPage === 1) {
            delete urlTree.queryParams['pg'];
        } else {
            urlTree.queryParams['pg'] = currentPage.toString();
        }

        this.updateQueryParams(urlTree.queryParams);
    }

    /**
     * Set current sorting query param.
     *
     * @param sort {string} - Current sort
     * @returns void
     */
    setSorting(sort?: string): void {
        const urlTree = this.getUrlTree();

        if (sort === undefined || sort === 'default') {
            delete urlTree.queryParams['sort'];
        } else if (sort !== 'default') {
            urlTree.queryParams['sort'] = sort;
        }

        this.updateQueryParams(urlTree.queryParams);
    }

    /**
     * Convert selection to query params.
     *
     * @param selection {AdvancedCommerceStore.Filters.Selection[]} - Selection
     * @returns {Params}
     */
    protected convertSelectionToQueryParams(selection: AdvancedCommerceStore.Filters.Selection[]): Params | undefined {
        const queryPrams: Params = {};

        selection.forEach(filter => {
            if (filter.items.length === 1) {
                const item = filter.items[0];

                if (filter.type === 'range') {
                    queryPrams[filter.id] = `${item.from || ''}-${item.to || ''}`;
                } else {
                    queryPrams[filter.id] = item.id;
                }
            } else if (filter.items.length > 1) {
                queryPrams[filter.id] = [];

                filter.items.forEach(item => {
                    if (filter.type === 'range') {
                        queryPrams[filter.id].push(`${item.from || ''}-${item.to || ''}`);
                    } else {
                        queryPrams[filter.id].push(item.id);
                    }
                });
            }
        });

        return Object.keys(queryPrams).length > 0 ? queryPrams : undefined;
    }

    protected getUrlTree(): UrlTree {
        return this.router.createUrlTree([this.document.location.origin], { queryParams: this.activatedRoute.snapshot.queryParams });
    }

    protected updateQueryParams(queryParams: Params) {
        const currentQueryParams = this.activatedRoute.snapshot.queryParams;

        if (!isEqual(queryParams, currentQueryParams)) {
            this.router.navigate([], {
                relativeTo: this.activatedRoute,
                queryParams
            });
        }
    }

    private get lang(): string {
        return this.advancedCommerceService.context.language_id?.split('-')[0] || 'en';
    }
}

export function injectRouteParams(): Signal<Params> {
    const params$ = inject(ActivatedRoute).queryParams;

    return toSignal(params$, {
        initialValue: {} as Record<string, string | undefined>
    });
}
